import { useCallback, useState } from 'react';
import { Button, Stack, Text } from '@chakra-ui/react';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth0 } from '@auth0/auth0-react';
import { createStripeCheckout } from '../api';

let stripePromise;
let cachedPublishableKey;

const getStripeClient = async (publishableKey) => {
    if (!publishableKey) {
        throw new Error('Missing Stripe publishable key.');
    }
    if (!stripePromise || publishableKey !== cachedPublishableKey) {
        cachedPublishableKey = publishableKey;
        stripePromise = loadStripe(publishableKey);
    }
    return stripePromise;
};

function UpgradeToPremium({ returnPath = '/ratings' }) {
    const { getAccessTokenSilently } = useAuth0();
    const [isCheckoutLoading, setCheckoutLoading] = useState(false);
    const [error, setError] = useState(null);

    const startCheckout = useCallback(async () => {
        if (isCheckoutLoading) return;
        setError(null);
        setCheckoutLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
            const safeReturnPath = returnPath.startsWith('/') ? returnPath : '/ratings';
            const separator = safeReturnPath.includes('?') ? '&' : '?';
            const successUrl = `${origin}${safeReturnPath}${separator}payment=success`;
            const cancelUrl = `${origin}${safeReturnPath}${separator}payment=canceled`;
            const checkout = await createStripeCheckout(token, { successUrl, cancelUrl });
            if (!checkout?.session_id || !checkout?.publishable_key) {
                throw new Error('Invalid checkout response.');
            }
            const stripe = await getStripeClient(checkout.publishable_key);
            const { error: stripeError } = await stripe.redirectToCheckout({
                sessionId: checkout.session_id,
            });
            if (stripeError) {
                throw new Error(stripeError.message || 'Stripe checkout failed.');
            }
        } catch (err) {
            console.error('Stripe checkout error', err);
            setError(err.message || 'Unable to start checkout. Please try again.');
            setCheckoutLoading(false);
        }
    }, [getAccessTokenSilently, isCheckoutLoading, returnPath]);

    return (
        <Stack gap={1}>
            <Button
                onClick={startCheckout}
                isLoading={isCheckoutLoading}
                colorPalette="yellow"
                fontWeight="semibold"
            >
                Upgrade to Premium
            </Button>
            {error && (
                <Text fontSize="xs" color="red.500">
                    {error}
                </Text>
            )}
        </Stack>
    );
}

export default UpgradeToPremium;
